import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { joiResolver } from '@hookform/resolvers/joi';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router-dom';

import { usePartnersReadMe, usePartnersUpdate } from '@/api/hooks';
import { type Partner } from '@/api/routes/partners/partners.api';
import Button from '@/components/Button';
import FormGlobalError from '@/components/forms/FormGlobalError';
import { getAccountEditTarget } from '@/domain/account/utils';
import ContactStep from '@/domain/onboarding/components/ContactStep';
import OrganizationStep from '@/domain/onboarding/components/OrganizationStep';
import { buildOnboardingSchema } from '@/domain/onboarding/schemas';
import { type OnboardingFormValues } from '@/domain/onboarding/types';
import {
  fromPartner,
  isOnboardingVisibleField,
  toPartnerAccountBody,
} from '@/domain/onboarding/utils';
import { useApiFormErrors, useServerErrorsStore } from '@/hooks/useApiFormErrors';
import { useTranslate } from '@/state/i18n';
import { ScreenRoutes } from '@/types/router';
import { withServerErrors } from '@/utils/errors';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  partner: Partner;
}

/**
 * `/account/edit` (P15): the owner edits the profile of an approved account in one page. The
 * account stays approved; the document and the legal name are shown locked (the API refuses them).
 */
const AccountEdit = ({ partner }: Props) => {
  const t = useTranslate('domain.account.AccountEdit');
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { data: account } = usePartnersReadMe();
  const { mutateAsync: update, isPending } = usePartnersUpdate();

  const serverErrors = useServerErrorsStore();
  const resolver = useMemo(
    () => withServerErrors(joiResolver(buildOnboardingSchema('edit')), serverErrors),
    [serverErrors],
  );
  const {
    control,
    handleSubmit,
    setError,
    getValues,
    setValue,
    formState: { isDirty },
  } = useForm<OnboardingFormValues>({
    resolver,
    mode: 'onTouched',
    defaultValues: fromPartner(partner),
  });
  const { applyApiError, globalError, clearGlobalError, clearApiErrors } =
    useApiFormErrors<OnboardingFormValues>({
      setError,
      getValues,
      serverErrors,
      isVisibleField: isOnboardingVisibleField,
      fallbackMessage: t('submitError'),
    });

  if (getAccountEditTarget(account) !== 'account') {
    return <Navigate to={ScreenRoutes.ACCOUNT} replace />;
  }

  const onSubmit = async (values: OnboardingFormValues) => {
    clearApiErrors();
    try {
      await update(toPartnerAccountBody(values));
      enqueueSnackbar(t('saved'), { variant: 'success' });
      navigate(ScreenRoutes.ACCOUNT, { replace: true });
    } catch (error) {
      applyApiError(error);
    }
  };

  return (
    <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} sx={sx.root}>
      <Stack gap={0.75}>
        <Typography component="h1" variant="h2">
          {t('title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('subtitle')}
        </Typography>
      </Stack>
      <FormGlobalError message={globalError} onClose={clearGlobalError} />
      <Paper variant="outlined" sx={sx.paper}>
        <Typography variant="subtitle1" fontWeight={700}>
          {t('organization')}
        </Typography>
        <OrganizationStep control={control} setValue={setValue} lockDocument />
      </Paper>
      <Paper variant="outlined" sx={sx.paper}>
        <Typography variant="subtitle1" fontWeight={700}>
          {t('contact')}
        </Typography>
        <ContactStep control={control} />
      </Paper>
      <Box sx={sx.footer}>
        <Button variant="text" onClick={() => navigate(ScreenRoutes.ACCOUNT)} disabled={isPending}>
          {t('cancel')}
        </Button>
        <Button type="submit" variant="contained" loading={isPending} disabled={!isDirty}>
          {t('submit')}
        </Button>
      </Box>
    </Box>
  );
};

export default AccountEdit;

const sx = {
  root: {
    width: '100%',
    maxWidth: 880,
    mx: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  paper: {
    p: { xs: 2.5, sm: 3.5 },
    borderRadius: radiusTokens.xxl,
    display: 'flex',
    flexDirection: 'column',
    gap: 2.5,
  },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 },
};
